import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Shield, CheckCircle2, XCircle, RefreshCw, Ban, FileText, Clock, Eye, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const STATUS_META = {
  pending_review: { label: "Pending review", tone: "amber", icon: Clock },
  verified:       { label: "Verified", tone: "emerald", icon: CheckCircle2 },
  rejected:       { label: "Rejected", tone: "rose", icon: XCircle },
  needs_update:   { label: "Needs update", tone: "cyan", icon: RefreshCw },
  expired:        { label: "Expired", tone: "zinc", icon: Clock },
  revoked:        { label: "Revoked", tone: "red", icon: Ban },
};

const TYPE_LABEL = {
  food_handler: "Food Handler",
  food_manager: "Food Manager",
  business_license: "Business License",
  sellers_permit: "Seller's Permit",
  health_permit: "Health Permit",
  cottage_food: "Cottage Food",
  professional_cert: "Professional Cert.",
  other: "Other",
  custom: "Custom",
};

const TABS = ["pending_review", "verified", "rejected", "needs_update", "expired", "revoked"];

export default function AdminCredentials() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);  // credential being reviewed
  const [reviewReason, setReviewReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/credentials", { params: { status: tab, limit: 100 } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load credentials");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const viewFile = async (cred) => {
    setActive(cred);
    setReviewReason(cred.reason || "");
    setFileUrl(null);
    try {
      const { data } = await api.post(`/credentials/${cred.credential_id}/file-signed-url`);
      setFileUrl(`${process.env.REACT_APP_BACKEND_URL}${data.signed_url}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open document");
    }
  };

  const review = async (action) => {
    if (!active) return;
    if ((action === "reject" || action === "needs_update" || action === "revoke") && !reviewReason.trim()) {
      return toast.error("Please add a short reason so the vendor understands.");
    }
    setBusy(true);
    try {
      await api.post(`/admin/credentials/${active.credential_id}/review`, {
        action, reason: reviewReason.trim() || null,
      });
      toast.success(`Credential ${action.replace("_", " ")}`);
      setActive(null);
      setFileUrl(null);
      setReviewReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="admin-credentials-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="admin-credentials-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-pink-400/30 bg-gradient-to-br from-pink-500/10 via-zinc-950 to-cyan-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-pink-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <Shield className="w-3 h-3" /> Vendor Trust
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Credential Review</h1>
        <p className="text-sm text-zinc-400 mt-1">Approve, reject, or request updates on submitted vendor credentials. All decisions are audited.</p>
      </header>

      {/* Status tabs */}
      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="admin-credentials-tabs">
        {TABS.map((t) => {
          const M = STATUS_META[t];
          const Icon = M.icon;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                tab === t ? "bg-pink-500 text-white" : "bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10"
              }`}
              data-testid={`admin-credentials-tab-${t}`}
            >
              <Icon className="w-3.5 h-3.5" /> {M.label}
            </button>
          );
        })}
      </div>

      {/* Item list */}
      {loading ? (
        <div className="text-center py-20 text-zinc-500 text-sm inline-flex items-center gap-2 justify-center w-full">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-10 text-center text-zinc-500 text-sm" data-testid="admin-credentials-empty">
          No credentials in this status.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const M = STATUS_META[c.status] || STATUS_META.pending_review;
            const Icon = M.icon;
            return (
              <button
                key={c.credential_id}
                onClick={() => viewFile(c)}
                className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3 text-left hover:bg-white/[0.05] transition-colors"
                data-testid={`admin-credential-row-${c.credential_id}`}
              >
                <div className={`w-10 h-10 rounded-xl grid place-items-center bg-${M.tone}-500/15 text-${M.tone}-300`}>
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-black tracking-tight">{TYPE_LABEL[c.type] || c.type}</p>
                    {c.custom_type_label && <span className="text-[10px] text-zinc-400">· {c.custom_type_label}</span>}
                    <span className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-${M.tone}-500/15 text-${M.tone}-200 border border-${M.tone}-400/30 inline-flex items-center gap-1`}>
                      <Icon className="w-3 h-3" /> {M.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-0.5">
                    Business <span className="font-mono text-zinc-300">{c.business_id}</span>
                    {c.issuing_organization && <> · {c.issuing_organization}</>}
                    {c.expires_at && <> · expires {new Date(c.expires_at).toLocaleDateString()}</>}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-0.5">Submitted {new Date(c.created_at).toLocaleString()}</p>
                </div>
                <Eye className="w-4 h-4 text-zinc-500 shrink-0 mt-1" />
              </button>
            );
          })}
        </div>
      )}

      {/* Review drawer */}
      {active && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/80 backdrop-blur-sm p-4" data-testid="admin-credential-review-modal">
          <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-3xl border border-pink-400/40 bg-zinc-950 p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black">Credential review</p>
                <h3 className="text-xl font-black tracking-tight mt-0.5">{TYPE_LABEL[active.type] || active.type}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Business <span className="font-mono">{active.business_id}</span> · Holder <span className="font-mono">{active.holder_user_id}</span>
                </p>
              </div>
              <button
                onClick={() => { setActive(null); setFileUrl(null); }}
                className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10"
                data-testid="admin-credential-review-close"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>

            {/* Document viewer */}
            <div className="rounded-2xl border border-white/10 bg-zinc-900 overflow-hidden">
              {fileUrl ? (
                active.file_mime === "application/pdf" ? (
                  <object data={fileUrl} type="application/pdf" width="100%" height="440px" data-testid="admin-credential-document-pdf">
                    <p className="text-sm text-zinc-400 p-4">PDF preview not supported. <a href={fileUrl} target="_blank" rel="noreferrer" className="text-pink-300 underline">Open in a new tab</a></p>
                  </object>
                ) : (
                  <img src={fileUrl} alt="credential" className="w-full max-h-[440px] object-contain bg-zinc-900" data-testid="admin-credential-document-img" />
                )
              ) : (
                <div className="h-40 grid place-items-center text-zinc-500 text-sm gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Requesting a signed URL…
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              {active.issuing_organization && <Kv label="Issuer" value={active.issuing_organization} />}
              {active.credential_number && <Kv label="Number" value={active.credential_number} />}
              {active.issue_date && <Kv label="Issued" value={active.issue_date} />}
              {active.expires_at && <Kv label="Expires" value={new Date(active.expires_at).toLocaleDateString()} />}
              {active.state_jurisdiction && <Kv label="Jurisdiction" value={active.state_jurisdiction} />}
            </div>

            {/* Review reason */}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">
                Reason (required for reject / needs update / revoke)
              </label>
              <Textarea
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder="Explain briefly so the vendor understands what to fix or update."
                rows={3}
                className="mt-1 bg-white/5 border-white/10 text-white rounded-xl text-sm"
                maxLength={400}
                data-testid="admin-credential-review-reason"
              />
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              {["needs_update", "reject", "revoke", "approve"].map((a) => {
                const isApprove = a === "approve";
                return (
                  <Button
                    key={a}
                    onClick={() => review(a)}
                    disabled={busy}
                    className={`h-9 px-4 rounded-full text-xs font-black inline-flex items-center gap-1.5 ${
                      isApprove
                        ? "bg-emerald-500 hover:bg-emerald-400 text-black"
                        : a === "revoke"
                          ? "bg-red-500 hover:bg-red-400 text-white"
                          : "bg-white/5 border border-white/10 hover:bg-white/10 text-white"
                    }`}
                    data-testid={`admin-credential-review-action-${a}`}
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                      isApprove ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                      a === "reject" ? <XCircle className="w-3.5 h-3.5" /> :
                      a === "revoke" ? <Ban className="w-3.5 h-3.5" /> :
                      <RefreshCw className="w-3.5 h-3.5" />
                    )}
                    {a === "needs_update" ? "Needs update" : a.charAt(0).toUpperCase() + a.slice(1)}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kv({ label, value }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-2">
      <p className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold">{label}</p>
      <p className="text-white font-semibold truncate">{value}</p>
    </div>
  );
}
